import { type Client } from '@microsoft/microsoft-graph-client';
import type {
  Employee,
  Project,
  Application,
  AppAssignment,
  DailySnapshot,
  ChangeLog,
  ChangeAction,
} from '@/models/types';
import { getListItems, createListItem } from './sharePointService';

/**
 * Captures a daily snapshot of all key org metrics.
 * Called automatically after user/license sync, or manually from Settings.
 * Only creates one snapshot per day (skips if today's already exists).
 */
export async function captureDailySnapshot(
  client: Client,
  capturedBy: string,
): Promise<boolean> {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  // Note: we always create a new snapshot on each sync to preserve history
  // even multiple captures on the same day are useful (pre/post sync)

  // Gather all current data
  const [employees, projects, apps, assignments] = await Promise.all([
    getListItems<Employee>(client, 'OC_Employees'),
    getListItems<Project>(client, 'OC_Projects'),
    getListItems<Application>(client, 'OC_Applications'),
    getListItems<AppAssignment>(client, 'OC_AppAssignments'),
  ]);

  const activeEmployees = employees.filter(
    (e) => e.fields.Status === 'Active',
  );
  const activeProjects = projects.filter(
    (p) => p.fields.Status === 'Active',
  );
  const activeAssignments = assignments.filter(
    (a) => a.fields.Status === 'Active',
  );

  // Calculate total monthly cost
  const appCostMap = new Map<string, number>();
  for (const app of apps) {
    appCostMap.set(app.id, app.fields.MonthlyCostPerLicense ?? 0);
  }

  let totalMonthlyCost = 0;
  for (const a of activeAssignments) {
    const cost = appCostMap.get(String(a.fields.AppId)) ?? 0;
    totalMonthlyCost += cost;
  }
  // Add per-org costs
  for (const app of apps) {
    if (app.fields.LicenseModel === 'PerOrg') {
      totalMonthlyCost += app.fields.MonthlyCostPerLicense ?? 0;
    }
  }

  // License data per SKU
  const licenseData: Record<string, { assigned: number; total: number; cost: number }> = {};
  for (const app of apps) {
    if (app.fields.IsAutoSynced && app.fields.MicrosoftSKU) {
      const assignedCount = activeAssignments.filter(
        (a) => a.fields.AppId === Number(app.id),
      ).length;
      licenseData[app.fields.MicrosoftSKU] = {
        assigned: assignedCount,
        total: 0, // will be filled from LicenseSync data
        cost: app.fields.MonthlyCostPerLicense ?? 0,
      };
    }
  }

  // Department costs
  const deptCosts: Record<string, number> = {};
  for (const emp of activeEmployees) {
    const dept = emp.fields.Department || 'Unknown';
    const empAssignments = activeAssignments.filter(
      (a) => a.fields.EmployeeId === Number(emp.id),
    );
    let empCost = 0;
    for (const a of empAssignments) {
      empCost += appCostMap.get(String(a.fields.AppId)) ?? 0;
    }
    deptCosts[dept] = (deptCosts[dept] ?? 0) + empCost;
  }

  await createListItem<DailySnapshot>(client, 'OC_DailySnapshot', {
    SnapshotDate: today,
    ActiveEmployees: activeEmployees.length,
    TotalEmployees: employees.length,
    ActiveProjects: activeProjects.length,
    TotalApplications: apps.length,
    TotalAppAssignments: activeAssignments.length,
    TotalMonthlyCost: totalMonthlyCost,
    LicenseData: JSON.stringify(licenseData),
    DepartmentCosts: JSON.stringify(deptCosts),
    CapturedBy: capturedBy,
  });

  return true;
}

/**
 * Log a change event for audit trail.
 */
export async function logChange(
  client: Client,
  action: ChangeAction,
  entity: string,
  entityId: string,
  entityName: string,
  details: string,
  changedBy: string,
): Promise<void> {
  await createListItem<ChangeLog>(client, 'OC_ChangeLog', {
    Timestamp: new Date().toISOString(),
    Action: action,
    Entity: entity,
    EntityId: entityId,
    EntityName: entityName,
    Details: details,
    ChangedBy: changedBy,
  });
}

/**
 * Get historical snapshots for a date range.
 */
export async function getSnapshots(
  client: Client,
  fromDate?: string,
  toDate?: string,
) {
  let filter = '';
  if (fromDate && toDate) {
    filter = `fields/SnapshotDate ge '${fromDate}' and fields/SnapshotDate le '${toDate}'`;
  } else if (fromDate) {
    filter = `fields/SnapshotDate ge '${fromDate}'`;
  }

  return getListItems<DailySnapshot>(client, 'OC_DailySnapshot', {
    filter: filter || undefined,
    orderBy: 'fields/SnapshotDate desc',
  });
}

/**
 * Get recent change log entries.
 */
export async function getRecentChanges(
  client: Client,
  top: number = 50,
) {
  return getListItems<ChangeLog>(client, 'OC_ChangeLog', {
    top,
    orderBy: 'fields/Timestamp desc',
  });
}
